import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AccessPolicy {
  id: number;
  name: string;
  description: string;
  targetType: 'Identity Group' | 'Api Group';
  target?: string;
  scopes: string[];
  status: 'Active' | 'Inactive' | 'Draft';
  createdDate: string;
}

export interface AccessPolicyRequest {
  name: string;
  description: string;
  targetType: 'Identity Group' | 'Api Group';
  scopes: string[];
  status: 'Active' | 'Inactive' | 'Draft';
}

export interface AccessPolicyResponse {
  content: AccessPolicy[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

// Identity and API Group models for target selection
export interface IdentityGroup {
  id: number;
  name: string;
  type: 'Dynamic' | 'Static' | 'Synced';
  members: number;
}

export interface ApiGroup {
  id: number;
  name: string;
  description: string;
  apiCount: number;
}

export interface Scope {
  name: string;
  description?: string;
  createdDate?: string;
}

export interface CreateScopeRequest {
  name: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AccessPolicyService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {}

  getAccessPolicies(
    page = 0,
    size = 20,
    query = '',
    targetType?: string,
    status?: string,
    sort?: string
  ): Observable<AccessPolicyResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (query) {
      params = params.set('query', query);
    }
    if (targetType) {
      params = params.set('targetType', targetType);
    }
    if (status) {
      params = params.set('status', status);
    }
    if (sort) {
      params = params.set('sort', sort);
    }

    return this.http.get<AccessPolicyResponse>(
      `${this.baseUrl}/admin/access-policies`,
      { params }
    );
  }

  getAccessPolicyById(id: number): Observable<AccessPolicy> {
    return this.http.get<AccessPolicy>(
      `${this.baseUrl}/admin/access-policies/${id}`
    );
  }

  createAccessPolicy(payload: AccessPolicyRequest): Observable<AccessPolicy> {
    return this.http.post<AccessPolicy>(
      `${this.baseUrl}/admin/access-policies`,
      payload
    );
  }

  updateAccessPolicy(id: number, payload: AccessPolicyRequest): Observable<AccessPolicy> {
    return this.http.put<AccessPolicy>(
      `${this.baseUrl}/admin/access-policies/${id}`,
      payload
    );
  }

  deleteAccessPolicy(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/admin/access-policies/${id}`
    );
  }

  getScopes(): Observable<Scope[]> {
    return this.http.get<Scope[]>(`${this.baseUrl}/admin/access-policies/scopes`);
  }

  createScope(payload: CreateScopeRequest): Observable<Scope> {
    return this.http.post<Scope>(`${this.baseUrl}/admin/access-policies/scopes`, payload);
  }
}